/**
 * Advertising in the world.
 *
 * Draws whatever is booked onto each slot, watches how long the player can
 * actually see it, and reports views in batches.
 *
 * Nothing here links out. Tapping a board opens a panel with the details and a
 * button to KEEP the offer; the list of kept offers lives outside the game.
 * There is no external navigation in the game client at all — not one it
 * chooses not to use, one it does not have.
 */

import type { Scene } from '@babylonjs/core/scene.js';
import type { Camera } from '@babylonjs/core/Cameras/camera.js';
import type { Mesh } from '@babylonjs/core/Meshes/mesh.js';
import { Color3 } from '@babylonjs/core/Maths/math.color.js';
import { Vector3, Matrix } from '@babylonjs/core/Maths/math.vector.js';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial.js';
import { Texture } from '@babylonjs/core/Materials/Textures/texture.js';
import { Ray } from '@babylonjs/core/Culling/ray.js';

import type { VisibilityRules, ViewClaim, Device } from '@emberreach/shared';
import { screenCoverage } from '@emberreach/shared';
import type { ServedAd } from '../api/gameTypes.js';

interface Board {
  ad: ServedAd;
  mesh: Mesh;
  centre: Vector3;
  /** The four corners in world space, so a rotated board measures correctly. */
  corners: readonly Vector3[];
  /** Continuous milliseconds visible. Reset the moment it stops being visible. */
  visibleMs: number;
  /** When this board last reported a view, so one long look reports once. */
  reportedAt: number;
  /* What the last frame measured. Kept so `focused()` can answer from the
     measurement already taken rather than casting a second ray. */
  lastDistanceM: number;
  lastFraction: number;
  lastUnobstructed: boolean;
}

const NEVER = -Infinity;

export class AdBoards {
  private readonly scene: Scene;
  private readonly boards = new Map<string, Board>();
  private readonly touched = new Set<Mesh>();
  private rules: VisibilityRules;
  private pending: ViewClaim[] = [];
  private device: Device = 'desktop';

  /** Called with a batch of views to report. */
  onViews: ((claims: ViewClaim[]) => void) | null = null;

  constructor(scene: Scene, rules: VisibilityRules) {
    this.scene = scene;
    this.rules = rules;
  }

  setRules(rules: VisibilityRules): void {
    this.rules = rules;
  }

  setDevice(device: Device): void {
    this.device = device;
  }

  /**
   * Puts the booked artwork on the slots.
   *
   * `slotMeshes` are the blank panels built with the zone. The texture is
   * swapped rather than the mesh replaced, so a campaign change costs one image
   * load and the world geometry is never rebuilt underneath the player.
   */
  apply(ads: ServedAd[], slotMeshes: Map<string, Mesh>): void {
    this.clear();

    for (const ad of ads) {
      const mesh = slotMeshes.get(ad.slotId);
      if (mesh === undefined) continue;

      const material = new StandardMaterial(`ad-live-${ad.slotId}`, this.scene);
      material.diffuseTexture = new Texture(ad.imageUrl, this.scene, true, false);
      // Lit a little by itself, so a billboard still reads at dusk without
      // having to hang a lamp on every slot.
      material.emissiveColor = new Color3(0.32, 0.32, 0.32);
      material.specularColor = new Color3(0, 0, 0);
      material.backFaceCulling = true;
      mesh.material = material;

      // A blank slot is scenery. A booked one can be tapped.
      mesh.isPickable = true;
      this.touched.add(mesh);

      /* Corners are taken from the mesh's own world matrix rather than by
         adding half-width to the centre in X. Slots are rotated to face the
         street, so a world-axis box would measure the wrong rectangle for
         every board that is not square-on to the map. */
      const world = mesh.computeWorldMatrix(true);
      const half = { w: ad.widthM / 2, h: ad.heightM / 2 };
      const corners = [
        [-half.w, -half.h], [half.w, -half.h], [half.w, half.h], [-half.w, half.h]
      ].map(([x, y]) =>
        Vector3.TransformCoordinates(new Vector3(x!, y!, 0), world)
      );

      this.boards.set(ad.slotId, {
        ad,
        mesh,
        centre: mesh.getAbsolutePosition().clone(),
        corners,
        visibleMs: 0,
        reportedAt: NEVER,
        lastDistanceM: Infinity,
        lastFraction: 0,
        lastUnobstructed: false
      });
    }
  }

  /**
   * One frame of visibility measurement.
   *
   * `tabActive` is passed in rather than read here. It is one of the checks
   * that decides whether an advertiser is billed, so it belongs somewhere
   * obvious rather than buried inside a render loop.
   */
  update(camera: Camera, dtMs: number, tabActive: boolean): void {
    if (this.boards.size === 0) return;

    /* Measured in render pixels, not CSS pixels, because that is the space
       Vector3.Project returns. The fraction is a ratio, so the two agree — but
       mixing them would make every reading wrong by the hardware scaling
       factor, which is exactly the quality setting a player is free to change. */
    const engine = this.scene.getEngine();
    const renderWidth = engine.getRenderWidth();
    const renderHeight = engine.getRenderHeight();
    const viewport = { width: renderWidth, height: renderHeight };
    const transform = this.scene.getTransformMatrix();
    const globalViewport = camera.viewport.toGlobal(renderWidth, renderHeight);
    const identity = Matrix.Identity();
    const now = performance.now();
    const dedupeMs = this.rules.dedupeWindowMinutes * 60 * 1000;

    for (const board of this.boards.values()) {
      const distance = Vector3.Distance(camera.position, board.centre);
      board.lastDistanceM = distance;
      // Cleared each frame, and set again only if the board really is on screen.
      board.lastFraction = 0;
      board.lastUnobstructed = false;

      // Cheapest test first, and it doubles as culling: past its stated viewing
      // distance a board neither counts nor needs drawing.
      if (distance > board.ad.viewDistanceM) {
        board.mesh.setEnabled(false);
        board.visibleMs = 0;
        continue;
      }
      board.mesh.setEnabled(true);

      /* Project the board's own corners, not its centre. That is what makes
         "half of it is on screen" mean something when the player is standing
         at the edge of the viewport looking sideways. */
      let left = Infinity, right = -Infinity, top = Infinity, bottom = -Infinity;
      let offCamera = false;
      for (const corner of board.corners) {
        const p = Vector3.Project(corner, identity, transform, globalViewport);
        // z outside 0..1 means behind the camera or past the far plane.
        if (p.z < 0 || p.z > 1) { offCamera = true; break; }
        left = Math.min(left, p.x);
        right = Math.max(right, p.x);
        top = Math.min(top, p.y);
        bottom = Math.max(bottom, p.y);
      }
      if (offCamera) {
        board.visibleMs = 0;
        continue;
      }

      const fraction = screenCoverage({ left, right, top, bottom }, viewport);
      const unobstructed = this.isUnobstructed(camera.position, board);
      board.lastFraction = fraction;
      board.lastUnobstructed = unobstructed;

      const visibleNow = tabActive && unobstructed && fraction >= this.rules.minScreenFraction;
      if (!visibleNow) {
        /* Continuous, not cumulative. A board glimpsed for 200 ms five times
           while running past is not the same as one looked at for a second,
           and an advertiser should not be charged as though it were. */
        board.visibleMs = 0;
        continue;
      }

      board.visibleMs += dtMs;
      if (board.visibleMs < this.rules.minVisibleMs) continue;
      // The server dedupes as well; this just stops the client shouting.
      if (now - board.reportedAt < dedupeMs) continue;

      board.reportedAt = now;
      this.pending.push({
        slotId: board.ad.slotId,
        campaignId: board.ad.campaignId,
        visibleMs: Math.round(board.visibleMs),
        distanceM: distance,
        screenFraction: fraction,
        unobstructed,
        tabActive,
        /* These three are sent for completeness of the type. The server
           overwrites all of them from the slot's own record and from the
           request headers, because a client is not allowed to tell us how far
           away its own billboard was allowed to be seen from. */
        slotViewDistanceM: board.ad.viewDistanceM,
        device: this.device,
        slotShowsOnDevice: true
      });
    }
  }

  /**
   * Is there anything solid between the player and the board?
   *
   * Standing behind the Compact Hall must not accrue visible time. Only meshes
   * marked as blockers are considered — grass tufts and fence rails should not
   * cost an advertiser a paid-for view.
   */
  private isUnobstructed(from: Vector3, board: Board): boolean {
    const direction = board.centre.subtract(from);
    const distance = direction.length();
    if (distance <= 0.2) return true;
    direction.normalize();

    // Stops just short of the board, so the board can never occlude itself.
    const ray = new Ray(from, direction, distance - 0.2);
    const pick = this.scene.pickWithRay(ray, (mesh) =>
      mesh !== board.mesh &&
      mesh.isEnabled() &&
      (mesh.metadata as { cameraBlocker?: boolean } | null)?.cameraBlocker === true
    );
    return pick?.hit !== true;
  }

  /** Hands over the batch of views to report and clears it. */
  flush(): void {
    if (this.pending.length === 0) return;
    const batch = this.pending;
    this.pending = [];
    this.onViews?.(batch);
  }

  /**
   * The board close enough and visible enough to offer to read.
   *
   * Proximity, not gaze. The rest of the game works this way — an exit, a
   * shopkeeper and an enemy are all offered by how near they are — and a
   * crosshair test fails on billboards anyway, because a sign four metres up
   * is never under the middle of the screen when you are standing at its foot.
   *
   * It still has to be on screen and not behind a wall, which is why this
   * answers from the measurement `update()` already took rather than casting a
   * second ray: one source of truth for "can this be seen".
   */
  focused(maxDistanceM = 16): ServedAd | null {
    let best: Board | null = null;
    for (const board of this.boards.values()) {
      if (board.lastDistanceM > maxDistanceM) continue;
      if (!board.lastUnobstructed) continue;
      // Any part of it on screen is enough to offer to read it. The stricter
      // fraction rule is for billing, not for a prompt.
      if (board.lastFraction <= 0.02) continue;
      if (best === null || board.lastDistanceM < best.lastDistanceM) best = board;
    }
    return best?.ad ?? null;
  }

  /**
   * Returns the slots to how the zone builder left them.
   *
   * The `adSlotId` metadata is the renderer's, not ours, so it stays. What has
   * to be undone is being pickable and any hiding done by distance culling —
   * a mesh left disabled here would vanish from the next campaign.
   */
  clear(): void {
    for (const mesh of this.touched) {
      if (mesh.isDisposed()) continue;
      mesh.isPickable = false;
      mesh.setEnabled(true);
    }
    this.touched.clear();
    this.boards.clear();
    this.pending = [];
  }
}
